import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { formatDistanceToNow, parseISO } from 'date-fns'
import { es } from 'date-fns/locale/es'

import { useAuthStore } from './useAuthStore'
import { useNotificationReadStore } from './useNotificationReadStore'
import { appointmentApi } from '@services/appointment.service'
import type { Appointment } from '@/types'

export type NotificationType = 'appointment_approval' | 'appointment_upcoming'

export interface AppNotification {
  id: string
  type: NotificationType
  title: string
  description: string
  /** ISO timestamp used for ordering (most recent / soonest first). */
  timestamp: string
  /** Relative, human-friendly time label already localized. */
  timeLabel: string
  href: string
  read: boolean
}

const POLL_INTERVAL = 60_000

function startOfToday(): Date {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  return d
}

function endOfToday(): Date {
  const d = new Date()
  d.setHours(23, 59, 59, 999)
  return d
}

/**
 * Aggregates actionable, real data the app already exposes into a unified
 * notification feed for the header bell. Read/dismiss state is persisted
 * locally via {@link useNotificationReadStore}.
 *
 * Sources:
 *  - Pending appointment approvals (doctors only)
 *  - Upcoming appointments for the rest of today
 */
export function useNotifications() {
  const { t, i18n } = useTranslation()
  const user = useAuthStore((s) => s.user)
  const dateLocale = i18n.language?.startsWith('es') ? es : undefined

  const readIds = useNotificationReadStore((s) => s.readIds)
  const dismissedIds = useNotificationReadStore((s) => s.dismissedIds)
  const markRead = useNotificationReadStore((s) => s.markRead)
  const markManyRead = useNotificationReadStore((s) => s.markManyRead)
  const dismiss = useNotificationReadStore((s) => s.dismiss)

  const isDoctor = user?.role === 'doctor'

  const { data: pendingData } = useQuery({
    queryKey: ['notifications', 'pending-approvals'],
    queryFn: () => appointmentApi.getPendingApprovals(0, 50),
    enabled: Boolean(user) && isDoctor,
    refetchInterval: POLL_INTERVAL,
  })

  const { data: upcomingData } = useQuery({
    queryKey: ['notifications', 'upcoming-appointments', user?.id, isDoctor],
    queryFn: () =>
      appointmentApi.getAppointments({
        date_from: startOfToday().toISOString(),
        date_to: endOfToday().toISOString(),
        doctor_id: isDoctor ? user?.id : undefined,
        limit: 50,
      }),
    enabled: Boolean(user),
    refetchInterval: POLL_INTERVAL,
  })

  const notifications = useMemo<AppNotification[]>(() => {
    const now = Date.now()
    const relTime = (iso: string) => {
      try {
        return formatDistanceToNow(parseISO(iso), { addSuffix: true, locale: dateLocale })
      } catch {
        return ''
      }
    }

    const items: AppNotification[] = []

    // Pending approvals — need the doctor's action.
    for (const appt of pendingData?.items ?? []) {
      const id = `approval:${appt.id}`
      if (dismissedIds[id]) continue
      items.push({
        id,
        type: 'appointment_approval',
        title: t('notifications.approval.title'),
        description: t('notifications.approval.desc', {
          patient: appt.patient_name || appt.patient?.first_name || t('common.patient', 'Paciente'),
          reason: appt.reason || '',
        }).trim(),
        timestamp: appt.appointment_date,
        timeLabel: relTime(appt.appointment_date),
        href: '/appointments',
        read: Boolean(readIds[id]),
      })
    }

    // Upcoming appointments still to come today.
    const cancelledStatuses = new Set(['cancelled', 'canceled', 'rejected', 'completed', 'no_show'])
    for (const appt of (upcomingData?.items ?? []) as Appointment[]) {
      const when = new Date(appt.appointment_date).getTime()
      if (Number.isNaN(when) || when < now) continue
      if (cancelledStatuses.has((appt.status || '').toLowerCase())) continue
      const id = `upcoming:${appt.id}`
      if (dismissedIds[id]) continue
      items.push({
        id,
        type: 'appointment_upcoming',
        title: t('notifications.upcoming.title'),
        description: t('notifications.upcoming.desc', {
          patient: appt.patient_name || appt.patient?.first_name || t('common.patient', 'Paciente'),
        }),
        timestamp: appt.appointment_date,
        timeLabel: relTime(appt.appointment_date),
        href: '/appointments',
        read: Boolean(readIds[id]),
      })
    }

    // Approvals first, then soonest appointments.
    return items.sort((a, b) => {
      if (a.type !== b.type) return a.type === 'appointment_approval' ? -1 : 1
      return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    })
  }, [pendingData, upcomingData, readIds, dismissedIds, t, dateLocale])

  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.read).length,
    [notifications]
  )

  const markAllRead = () => markManyRead(notifications.map((n) => n.id))

  return { notifications, unreadCount, markRead, markAllRead, dismiss }
}
